import React from "react";
import {
  ArrowRight,
  ArrowRightLeft,
  Building2,
  CheckCircle2,
  Database,
  FileCheck2,
  History,
  Landmark,
  ListChecks,
  QrCode,
  ReceiptText,
  ShieldCheck,
  Smartphone,
  WalletCards,
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageProvider";
import RevealOnScroll from "../RevealOnScroll";

const paymentSteps = [
  {
    icon: QrCode,
    title: "Le client s’identifie",
    description:
      "Le client scanne son QR Code WonyaPay ou sélectionne la facture qu’il souhaite régler.",
    details: [
      "Identification du payeur",
      "Accès sécurisé à son profil",
      "Récupération des factures associées",
    ],
    label: "Identification",
  },
  {
    icon: ReceiptText,
    title: "WonyaPay affiche les opérations disponibles",
    description:
      "L’application affiche les factures à payer, les paiements déjà effectués et les informations utiles au règlement.",
    details: [
      "Factures en attente",
      "Paiements déjà réalisés",
      "Détails et historique du payeur",
    ],
    label: "Consultation",
  },
  {
    icon: WalletCards,
    title: "Le client choisit son moyen de paiement",
    description:
      "Le client sélectionne l’un des moyens de paiement disponibles et confirme la transaction.",
    details: [
      "Mobile Money",
      "Carte bancaire",
      "Autres canaux activés pour le service",
    ],
    label: "Choix du canal",
  },
  {
    icon: ShieldCheck,
    title: "WonyaPay traite et sécurise la transaction",
    description:
      "La demande est transmise au canal sélectionné, vérifiée puis confirmée en temps réel selon la disponibilité du canal de paiement.",
    details: [
      "Transmission sécurisée",
      "Vérification du statut",
      "Référence de transaction",
    ],
    label: "Confirmation",
  },
  {
    icon: Database,
    title: "Le paiement est enregistré dans le circuit de collecte",
    description:
      "Après confirmation, l’opération est enregistrée, la facture est mise à jour et les fonds suivent le circuit de collecte configuré pour le service.",
    details: [
      "Mise à jour de la facture",
      "Traçabilité de l’opération",
      "Préparation du reversement",
    ],
    label: "Collecte configurée",
  },
  {
    icon: ArrowRightLeft,
    title:
      "Les fonds sont reversés sur le compte Mobile Money ou bancaire de votre organisation",
    description:
      "WonyaPay communique avec les systèmes partenaires afin d’ordonner le reversement vers le compte Mobile Money ou le compte bancaire configuré par votre organisation.",
    details: [
      "Déclenchement du reversement",
      "Rapprochement avec la transaction d’origine",
      "Historique disponible dans la caisse Business",
    ],
    note: "Le délai effectif de crédit dépend du canal, de la configuration du service et de la disponibilité des systèmes partenaires.",
    label: "Compte partenaire",
    confirmed: true,
  },
];

const summaryItems = [
  { icon: QrCode, label: "QR Code" },
  { icon: Smartphone, label: "WonyaPay" },
  { icon: WalletCards, label: "Moyen de paiement" },
  { icon: ShieldCheck, label: "Confirmation" },
  { icon: Database, label: "Circuit de collecte" },
  { icon: ArrowRightLeft, label: "Reversement" },
  { icon: Building2, label: "Caisse Business" },
];

const accountFields = [
  { icon: CheckCircle2, label: "Paiement confirmé" },
  { icon: FileCheck2, label: "Facture mise à jour" },
  { icon: ReceiptText, label: "Référence : TX-••••••" },
  { icon: WalletCards, label: "Canal de paiement" },
  { icon: ArrowRightLeft, label: "Statut du reversement" },
  { icon: History, label: "Historique disponible" },
];

const trustItems = [
  { icon: ShieldCheck, label: "Transaction sécurisée" },
  { icon: ListChecks, label: "Suivi du statut" },
  { icon: History, label: "Traçabilité complète" },
  { icon: Landmark, label: "Reversement vers le compte du partenaire" },
];

const PaymentFlowSection = ({ onStartRegistration }) => {
  const { t } = useLanguage();

  return (
    <section
      className="ws-section ws-business-payment"
      aria-labelledby="ws-business-payment-title"
    >
      <div className="ws-shell">
        <RevealOnScroll className="ws-section-heading ws-section-heading--center">
          <span className="ws-kicker">
            <QrCode size={16} aria-hidden="true" /> {t("Processus de paiement")}
          </span>
          <h2 id="ws-business-payment-title">
            {t("Du paiement du client à votre caisse Business")}
          </h2>
          <p>
            {t(
              "Un parcours simple, sécurisé et traçable, depuis l’identification du payeur jusqu’au reversement des fonds dans le compte de votre organisation.",
            )}
          </p>
          <p className="ws-business-payment__intro-note">
            {t(
              "WonyaPay centralise l’identification, le paiement, la confirmation, le suivi et le reversement dans un même processus.",
            )}
          </p>
        </RevealOnScroll>

        <RevealOnScroll as="ol" className="ws-payment-flow">
          {paymentSteps.map(
            (
              {
                icon: Icon,
                title,
                description,
                details,
                note,
                label,
                confirmed,
              },
              index,
            ) => (
              <RevealOnScroll
                as="li"
                className={confirmed ? "is-confirmed" : ""}
                delay={index * 80}
                key={title}
                variant={index % 2 === 0 ? "fade-right" : "fade-left"}
              >
                <span className="ws-payment-flow__marker" aria-hidden="true">
                  <Icon size={22} />
                  <small>{index + 1}</small>
                </span>
                <article className="ws-payment-flow__card">
                  <div className="ws-payment-flow__card-head">
                    <span>{t(`Étape ${index + 1}`)}</span>
                    <span>{t(label)}</span>
                  </div>
                  <h3>{t(title)}</h3>
                  <p>{t(description)}</p>
                  <ul>
                    {details.map((detail) => (
                      <li key={detail}>
                        <CheckCircle2 size={16} aria-hidden="true" />
                        <span>{t(detail)}</span>
                      </li>
                    ))}
                  </ul>
                  {note && <p className="ws-payment-flow__note">{t(note)}</p>}
                </article>
              </RevealOnScroll>
            ),
          )}
        </RevealOnScroll>

        <RevealOnScroll className="ws-payment-flow__summary" variant="scale-in">
          {summaryItems.map(({ icon: Icon, label }, index) => (
            <React.Fragment key={label}>
              <span>
                <Icon size={17} aria-hidden="true" />
                {t(label)}
              </span>
              {index < summaryItems.length - 1 && (
                <ArrowRight
                  className="ws-payment-flow__summary-arrow"
                  size={17}
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          ))}
        </RevealOnScroll>

        <div className="ws-payment-flow__outcome">
          <RevealOnScroll
            className="ws-business-payment__assurance"
            variant="fade-right"
          >
            <span>
              <ShieldCheck size={28} aria-hidden="true" />
            </span>
            <div>
              <h3>{t("Votre argent reste destiné à votre activité")}</h3>
              <p>
                {t(
                  "WonyaPay orchestre et trace le paiement, mais ne conserve pas définitivement les fonds de votre organisation. Après confirmation, les fonds suivent le circuit de collecte et de reversement configuré vers le compte Mobile Money ou le compte bancaire de votre entreprise, institution, école, commerce ou service.",
                )}
              </p>
              <ul>
                <li>{t("Traçabilité de chaque opération")}</li>
                <li>{t("Rapprochement avec la facture concernée")}</li>
                <li>{t("Historique disponible dans la caisse Business")}</li>
              </ul>
              <button
                className="ws-button ws-button--primary ws-business-payment__registration"
                type="button"
                onClick={onStartRegistration}
                aria-haspopup="dialog"
              >
                {t("Commencer l’inscription")}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            </div>
          </RevealOnScroll>

          <RevealOnScroll
            as="div"
            role="group"
            className="ws-business-account-preview"
            aria-label={t("Aperçu de la caisse Business")}
            variant="fade-left"
          >
            <div className="ws-business-account-preview__head">
              <div>
                <span className="ws-kicker">{t("Caisse Business")}</span>
                <h3>{t("Opération après reversement")}</h3>
              </div>
              <CheckCircle2 size={26} aria-hidden="true" />
            </div>
            <ul>
              {accountFields.map(({ icon: Icon, label }) => (
                <li key={label}>
                  <Icon size={17} aria-hidden="true" />
                  <span>{t(label)}</span>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </div>

        <div
          className="ws-payment-flow__trust"
          aria-label={t("Garanties du parcours de paiement")}
        >
          {trustItems.map(({ icon: Icon, label }, index) => (
            <RevealOnScroll
              as="div"
              delay={index * 70}
              key={label}
              variant="scale-in"
            >
              <Icon size={20} aria-hidden="true" />
              <span>{t(label)}</span>
            </RevealOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PaymentFlowSection;
